import { useEffect, useState } from "react";
import { useReducedMotion } from "framer-motion";
import type { KnowledgeCard, RecallCoordinate } from "@/types/journey";

const THEMES = ["first", "struggle", "dedication"] as const;
const LABELS = ["第一次选择", "第二次选择", "最后留下的话"];

/** 句读后稍作停顿，读起来像一句一句说出来，而不是匀速刷字。 */
const PAUSE_AFTER = /[，。、；：！？…]/u;
const HEADLINE_STEP_MS = 46;
const BODY_STEP_MS = 26;
const BODY_PAUSE_MS = 150;
const FIRST_DELAY_MS = 140;

type TypingPhase = "headline" | "body" | "done";

/**
 * 逐字呈现后端 AI 的回望：先小标题，再正文，句读处停顿。
 * 关闭动效偏好时直接给全文。
 */
function useTypedCoordinate(value: RecallCoordinate | null | undefined, enabled: boolean) {
  const headline = value?.headline ?? "";
  const body = value?.connection ?? "";
  const [headlineCount, setHeadlineCount] = useState(() => (enabled ? 0 : [...headline].length));
  const [bodyCount, setBodyCount] = useState(() => (enabled ? 0 : [...body].length));
  const [phase, setPhase] = useState<TypingPhase>(() => (enabled ? "headline" : "done"));

  useEffect(() => {
    const headlineChars = [...headline];
    const bodyChars = [...body];
    if (!enabled || !headlineChars.length) {
      setHeadlineCount(headlineChars.length);
      setBodyCount(bodyChars.length);
      setPhase("done");
      return;
    }
    let cancelled = false;
    let timer = 0;
    let index = 0;
    setHeadlineCount(0);
    setBodyCount(0);
    setPhase("headline");

    const step = () => {
      if (cancelled) return;
      if (index < headlineChars.length) {
        setHeadlineCount(index + 1);
        index += 1;
        timer = window.setTimeout(step, HEADLINE_STEP_MS);
        return;
      }
      const bodyIndex = index - headlineChars.length;
      if (bodyIndex < bodyChars.length) {
        setBodyCount(bodyIndex + 1);
        setPhase("body");
        index += 1;
        const character = bodyChars[bodyIndex];
        timer = window.setTimeout(step, PAUSE_AFTER.test(character ?? "") ? BODY_PAUSE_MS : BODY_STEP_MS);
        return;
      }
      setPhase("done");
    };

    timer = window.setTimeout(step, FIRST_DELAY_MS);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [body, enabled, headline]);

  return {
    headline: [...headline].slice(0, headlineCount).join(""),
    body: [...body].slice(0, bodyCount).join(""),
    phase,
  };
}

export function JourneyRecall({ answers, cards, pending, busy, error, onRetry, step, onStep, onContinue, coordinate, aiPending, aiError, onAiRetry }: {
  answers: string[]; cards: KnowledgeCard[]; pending: boolean; busy: boolean; error: boolean;
  onRetry: () => void; step: number; onStep: (step: number) => void; onContinue: () => void;
  coordinate?: { value: RecallCoordinate; source: string } | null; aiPending?: boolean; aiError?: boolean; onAiRetry?: () => void;
}) {
  const reduceMotion = useReducedMotion();
  const typed = useTypedCoordinate(coordinate?.value, !reduceMotion);
  const card = cards.filter(card => card.review_status === "published" && card.environment === "production")
    .filter(card => card.theme === THEMES[step]).sort((a, b) => b.version - a.version)[0];
  return <section className="v2-scene v2-theory v2-journey-recall" data-testid="v2-theory">
    <div className="v2-recall-heading"><p>湖面静下来，再看一路的选择。</p><h1 tabIndex={-1}>三次回望</h1></div>
    <nav className="v2-wake-nodes" aria-label="回望三次选择">
      {LABELS.map((label, index) => <button key={label} type="button" aria-current={step === index ? "step" : undefined}
        onClick={() => onStep(index)}><span aria-hidden="true">0{index + 1}</span>{label}</button>)}
    </nav>
    <div className="v2-recall-entry" key={step} aria-live="polite">
      <p className="v2-recall-caption">{LABELS[step]}，你留下的是——</p>
      <blockquote>{answers[step] || "这一程还没有留下回答。"}</blockquote>
      <div className="v2-recall-coordinate">
        <p>再读这一程的精神坐标</p>
        {card ? <><h2>{card.title}</h2>
          {/* 后端 AI 针对这一步回答写出的回望；逐字呈现，关闭动效时直接给全文。
              等待期间不显示任何"正在读……"之类的占位句，只在这一步确实失败时给出重试 */}
          {coordinate
            ? <div className="v2-recall-ai" data-source={coordinate.source} data-testid="v2-recall-ai">
                <p className="v2-recall-ai-headline" aria-hidden="true">{typed.headline}
                  {typed.phase === "headline" ? <i className="v2-typing-caret" /> : null}</p>
                <p className="v2-recall-ai-body" aria-hidden="true">{typed.body}
                  {typed.phase === "body" ? <i className="v2-typing-caret" /> : null}</p>
                <span className="v2-sr-only">{coordinate.value.headline}。{coordinate.value.connection}</span>
              </div>
            : aiError && !aiPending && onAiRetry
              ? <div className="v2-recall-ai is-pending" role="status">
                  <p>这一步的回望没能取回，你可以重试。</p>
                  <button className="v2-inline-retry" type="button" onClick={onAiRetry}>重新回望</button>
                </div>
              : null}
          <details className="v2-source-details"><summary>了解这份精神坐标</summary>
            {/* 展开细读的正文同样来自后端 AI（依审核原文展开），来源标注审核出处 */}
            <p className="v2-coordinate-reading">{coordinate?.value.elaboration ?? card.content}</p>
            {card.source_name || card.source_url
              ? <small className="v2-coordinate-source">{card.source_url ? <a href={card.source_url} target="_blank" rel="noreferrer">来源：{card.source_name || card.source_url}</a> : `来源：${card.source_name}`}</small>
              : null}
          </details></> : <div role="status"><p>{pending ? "正在取回精神坐标……" : error ? "这次没能加载精神坐标，你可以重试。" : "这一程暂时没有可展示的精神坐标，你可以继续回望。"}</p>
          {error && !pending ? <button className="v2-inline-retry" type="button" onClick={onRetry}>重新加载</button> : null}</div>}
      </div>
    </div>
    <div className="v2-recall-controls">
      {step > 0 ? <button type="button" onClick={() => onStep(step - 1)}>上一段回望</button> : <span />}
      {step < 2 ? <button type="button" onClick={() => onStep(step + 1)}>下一段回望 <span>→</span></button>
        : <button type="button" disabled={busy} onClick={onContinue}>{busy ? "正在寻找现实中的回应……" : "看看嘉兴的答案"} <span>→</span></button>}
    </div>
  </section>;
}
